import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Select } from '@/components/ui/Select';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { formatInteger } from '@/lib/format';
import { Can } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import {
  optionToYesNo,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
  yesNoToOption,
} from '@/hooks/useUrlState';
import { BLUEPRINT_COLUMN_SORT, BLUEPRINTS_DEFAULT_SORT, blueprintsUrlSchema } from './blueprintsUrlState';

const BlueprintsDoc = graphql(/* GraphQL */ `
  query Blueprints(
    $filter: BlueprintFilter
    $page: Int
    $pageSize: Int
    $sort: BlueprintSort
  ) {
    blueprints(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        title
        requireTicket
        minimumAge
        nightCount
        doorsOpen
        doorsClose
        isActive
        updatedAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

interface BlueprintRow {
  id: string;
  title: string;
  requireTicket: boolean;
  minimumAge?: number | null;
  nightCount: number;
  doorsOpen: string;
  doorsClose: string;
  isActive: boolean;
}

export function BlueprintsListPage() {
  const navigate = useNavigate();
  const { state: url, patch, setFilters } = useUrlState(blueprintsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, BLUEPRINTS_DEFAULT_SORT));
  };

  const sort = useMemo(
    () => ({ field: BLUEPRINT_COLUMN_SORT[url.sort], direction: (url.dir === 'desc' ? 'DESC' : 'ASC') as 'ASC' | 'DESC' }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading, error } = useQuery(BlueprintsDoc, {
    variables: {
      filter: {
        search: url.search || null,
        requireTicket: url.ticketed,
        isActive: url.active,
      },
      page: url.page,
      pageSize: url.size,
      sort,
    },
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;

  const rows: BlueprintRow[] = useMemo(
    () =>
      (viewData?.blueprints.nodes ?? []).map((n) => ({
        id: n.id,
        title: n.title,
        requireTicket: n.requireTicket,
        minimumAge: n.minimumAge,
        nightCount: n.nightCount,
        doorsOpen: n.doorsOpen,
        doorsClose: n.doorsClose,
        isActive: n.isActive,
      })),
    [viewData],
  );
  const pageInfo = viewData?.blueprints.pageInfo;

  const columns = useMemo<ColumnDef<BlueprintRow>[]>(
    () => [
      {
        id: 'title',
        // An accessor lets TanStack read the column's type: a string column sorts ascending first, so one click
        // reverses the grid's initial ascending order (without it the first click removed the sort).
        accessorKey: 'title',
        header: 'Blueprint',
        cell: ({ row }) => (
          <Link
            to={`/blueprints/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.title}
          </Link>
        ),
      },
      {
        id: 'ticket',
        header: 'Tickets',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={row.original.requireTicket ? 'brand' : 'neutral'}>
            {row.original.requireTicket ? 'Ticketed' : 'Free entrance'}
          </Badge>
        ),
      },
      {
        id: 'doors',
        header: 'Default doors',
        enableSorting: false,
        cell: ({ row }) =>
          `${row.original.doorsOpen}–${row.original.doorsClose}`,
      },
      {
        id: 'nights',
        header: 'Nights',
        enableSorting: false,
        cell: ({ row }) => formatInteger(row.original.nightCount),
      },
      {
        id: 'isActive',
        header: 'Active',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [listHref],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Blueprints
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Blueprints with <strong>no date</strong>. Default stages have no sale
            window. Plan dated nights on the night calendar.
          </p>
        </div>
        <Can permission="blueprint:manage">
          <Button
            onClick={() => navigate('/blueprints/new', { state: { from: listHref } })}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New blueprint
          </Button>
        </Can>
      </div>

      {error ? <QueryErrorNotice error={error} title="Could not load blueprints" /> : null}

      <FilterBar
        search={{ id: 'admin.blueprints.search', value: search, onChange: setSearch, placeholder: 'Search blueprints…' }}
        primary={
          <>
            <Select
              id="admin.blueprints.ticketed"
              value={yesNoToOption(url.ticketed)}
              onChange={(e) => setFilters({ ticketed: optionToYesNo(e.target.value) })}
              options={[
                { value: '', label: 'Any' },
                { value: 'yes', label: 'Ticketed' },
                { value: 'no', label: 'Free entrance' },
              ]}
            />
            <Select
              id="admin.blueprints.activeFilter"
              value={yesNoToOption(url.active)}
              onChange={(e) => setFilters({ active: optionToYesNo(e.target.value) })}
              options={[
                { value: '', label: 'Any status' },
                { value: 'yes', label: 'Active' },
                { value: 'no', label: 'Inactive' },
              ]}
            />
          </>
        }
      />

      <DataGrid<BlueprintRow>
        data={rows}
        columns={columns}
        loading={loading && !viewData}
        rowId={(row) => row.id}
        rowMuted={(row) => !row.isActive}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
